/**
 * From a problem+json `code` to a sentence somebody creating a project can act on.
 *
 * Same rule as `features/entrance/problems.ts` and `features/setup/problems.ts`: the mapping
 * is on `code`, never on the status — the contract `backend/src/kalqa/core/errors.py` states
 * in its first paragraph.
 *
 * **`conflict` is a field error, not a form error, and that is the one judgement here.**
 * `create_project` raises exactly one `Conflict`, from `uq_project_workspace_name`
 * (`projects/service.py:create_project`), so on this endpoint a `409 conflict` always means
 * "that name is taken" and belongs under the name box. The other 409 this call can produce
 * carries a *different* code — `idempotency_key_reused`, which is a client bug and not the
 * person's — which is why branching on the code rather than the status is what makes the
 * distinction possible at all.
 */

import { ApiError } from '@/api/client';
import type { MessageKeys } from '@/lib/i18n';

type ProjectsKey = MessageKeys['projects'];

/**
 * Codes from `ErrorCode` in `backend/src/kalqa/core/errors.py`.
 *
 * TWIN: that enum is the source. A code added there that B2 can provoke needs a line here, or
 * it silently becomes "the project could not be created".
 */
const BY_CODE: Readonly<Record<string, ProjectsKey>> = {
  forbidden: 'drawer.error.forbidden',
  rate_limited: 'drawer.error.rateLimited',
  validation_failed: 'drawer.error.validation',
  csrf_failed: 'drawer.error.unexpected',
  // A key reused with a different body. Nothing the person did — the drawer mints a fresh key
  // whenever a field changes — so it reads as the generic failure rather than as their fault.
  idempotency_key_reused: 'drawer.error.unexpected',
};

/** The block-level message key for a failed create. `conflict` never reaches here. */
export function createProjectErrorKey(error: unknown): ProjectsKey {
  if (!(error instanceof ApiError)) return 'drawer.error.unexpected';
  return BY_CODE[error.code] ?? 'drawer.error.unexpected';
}

/** `true` when the workspace already has a project by that name. */
export function isDuplicateName(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'conflict';
}

/**
 * The same mapping for the phase surface (`KLQ-20260827-002`).
 *
 * Its own function rather than a second entry in `BY_CODE`, because the *same code means a
 * different sentence here*: a `validation_failed` on a create is "check the fields", and on a
 * phase it is almost always the one rule the form has — a second instance of a rung with no
 * label. Sharing the table would have made that message impossible to write.
 */
const PHASE_BY_CODE: Readonly<Record<string, ProjectsKey>> = {
  forbidden: 'phase.error.forbidden',
  validation_failed: 'phase.error.labelRequired',
  conflict: 'phase.error.inUse',
  rate_limited: 'drawer.error.rateLimited',
};

export function phaseErrorKey(error: unknown): ProjectsKey {
  if (!(error instanceof ApiError)) return 'phase.error.unexpected';
  return PHASE_BY_CODE[error.code] ?? 'phase.error.unexpected';
}

/**
 * `true` when a phase could not be deleted because drawings are filed under it.
 *
 * The vocabulary rule of `MCK-20260813-003` §2: a referenced entry archives, it never vanishes
 * from under the rows that point at it. So this is a fork in the flow — offer the archive —
 * and not an error to report.
 */
export function isPhaseInUse(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'conflict';
}
